<template>
  <!-- Listener unifié pour l'ouverture du dialog de paiement -->
  <VBusHelper :reactOn="$constants.EVENTS.PAYMENT.OPEN" :handler="handleOpen" />

  <DialogComponent ref="dialog" type="modal">
    <div class="space-y-6 max-w-lg mx-auto">
      <!-- Header Dynamique -->
      <div class="text-center mb-6">
        <div class="inline-block p-4 rounded-full bg-primary-100 mb-4">
          <ColorImg :src="headerIcon" class="bg-primary-600 w-12 h-12" />
        </div>
        <h3 class="text-2xl font-bold text-gray-800 mb-2">
          {{ title }}
        </h3>
        <p class="text-gray-600">
          {{ subtitle }}
        </p>
      </div>

      <!-- Étape 1 : Formulaire -->
      <form
        v-if="currentStep === 'form'"
        @submit.prevent="handleNextStep"
        class="space-y-5"
      >
        <!-- Informations Client -->
        <div class="grid grid-cols-1 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Nom complet</label
            >
            <input
              v-model="form.fullName"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
              placeholder="Votre nom et prénom"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Email</label
              >
              <input
                v-model="form.email"
                type="email"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                placeholder="votre@email.com"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Téléphone (WhatsApp)</label
              >
              <input
                v-model="form.phone"
                type="tel"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                placeholder="+XXX XX XX XX XX"
              />
            </div>
          </div>
        </div>

        <!-- Choix du Produit / Niveau (si applicable) -->
        <div v-if="availableProducts.length > 1">
          <label class="block text-sm font-medium text-gray-700 mb-3"
            >Sélectionnez votre niveau</label
          >
          <div class="space-y-3">
            <div
              v-for="product in availableProducts"
              :key="product.id"
              @click="selectedProductId = product.id"
              class="relative flex items-center p-4 border rounded-xl cursor-pointer transition-all hover:shadow-md"
              :class="
                selectedProductId === product.id
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : 'border-gray-200'
              "
            >
              <div class="flex-1">
                <div class="flex justify-between items-center mb-1">
                  <span class="font-bold text-gray-800">{{
                    product.label
                  }}</span>
                  <span
                    class="text-xs font-bold px-2 py-1 rounded-full"
                    :class="
                      product.amount === 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-primary-100 text-primary-800'
                    "
                  >
                    {{ product.formattedPrice }}
                  </span>
                </div>
                <p class="text-sm text-gray-600">{{ product.description }}</p>
              </div>
              <div class="ml-3">
                <div
                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                  :class="
                    selectedProductId === product.id
                      ? 'border-primary-600'
                      : 'border-gray-300'
                  "
                >
                  <div
                    v-if="selectedProductId === product.id"
                    class="w-2.5 h-2.5 bg-primary-600 rounded-full"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Résumé du produit sélectionné (si un seul) -->
        <div
          v-else-if="currentProduct"
          class="bg-gray-50 p-4 rounded-xl border border-gray-200"
        >
          <div class="flex justify-between items-center mb-2">
            <span class="font-bold text-gray-800">{{
              currentProduct.label
            }}</span>
            <span class="font-bold text-primary-600 text-lg">{{
              currentProduct.formattedPrice
            }}</span>
          </div>
          <p class="text-sm text-gray-600">{{ currentProduct.description }}</p>
        </div>

        <!-- Zone d'action -->
        <div class="pt-4">
          <button
            type="submit"
            :disabled="!selectedProductId"
            class="w-full bg-primary-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-primary-700 transition-all disabled:opacity-50 shadow-lg shadow-primary-200"
          >
            {{
              context === 'donations'
                ? 'Faire mon don'
                : "S'inscrire maintenant"
            }}
          </button>

          <p class="text-center text-xs text-gray-500 mt-4">
            <ColorImg
              src="/images/icons/shield-check.svg"
              class="bg-green-500 w-4 h-4 inline-block mr-1"
            />
            Paiement sécurisé. Vos données sont protégées.
          </p>
        </div>
      </form>

      <!-- Étape 2 : Choix du prestataire -->
      <div v-else-if="currentStep === 'providers'" class="space-y-6">
        <div class="text-center">
          <p class="text-gray-600 mb-6">
            {{
              context === 'donations'
                ? 'Comment souhaitez-vous régler votre don ?'
                : 'Comment souhaitez-vous régler votre inscription ?'
            }}
          </p>
          <div class="grid grid-cols-1 gap-4">
            <div
              v-for="provider in availableProviders"
              :key="provider.id"
              @click="handleFinalPayment(provider.id)"
              class="flex items-center p-5 border-2 rounded-2xl cursor-pointer transition-all hover:border-primary-500 hover:bg-primary-50 group"
              :class="
                isProcessing && selectedProviderId === provider.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-100'
              "
            >
              <div
                class="w-16 h-16 flex items-center justify-center bg-white rounded-xl mr-5 shadow-sm border border-gray-50 transition-colors overflow-hidden p-2"
              >
                <ImgComponent
                  :src="provider.icon"
                  class="h-full w-full object-contain"
                />
              </div>
              <div class="flex-1 text-left">
                <h4 class="font-bold text-gray-800 text-lg leading-tight">
                  {{ provider.label }}
                </h4>
                <p class="text-xs text-gray-500 mt-1">
                  {{ provider.subtitle }}
                </p>
              </div>
              <div
                class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors"
              >
                <div
                  v-if="isProcessing && selectedProviderId === provider.id"
                  class="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"
                ></div>
                <ColorImg
                  v-else
                  src="/images/icons/chevron-right.svg"
                  class="bg-gray-400 w-4 h-4 group-hover:bg-primary-600"
                />
              </div>
            </div>
          </div>

          <button
            @click="currentStep = 'form'"
            class="mt-8 text-sm text-gray-500 hover:text-primary-600 underline"
          >
            Retour aux informations
          </button>
        </div>
      </div>

      <!-- Message de succès -->
      <div
        v-if="currentStep === 'success'"
        class="absolute inset-0 bg-white z-20 flex flex-col items-center justify-center text-center p-8 rounded-2xl"
      >
        <div
          class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6"
        >
          <ColorImg src="/images/icons/check.svg" class="bg-green-600 w-10 h-10" />
        </div>
        <h4 class="text-2xl font-bold text-gray-800 mb-2">
          {{
            context === 'donations'
              ? 'Merci pour votre don !'
              : 'Félicitations !'
          }}
        </h4>
        <p class="text-gray-600 mb-8">
          {{
            context === 'donations'
              ? 'Votre don pour '
              : 'Votre inscription à '
          }}<strong>{{ currentProduct?.label }}</strong> a été enregistrée avec
          succès.
        </p>
        <button
          @click="closeDialog"
          class="px-8 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-black transition-all"
        >
          Fermer
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { getCurrentInstance } from 'vue';
import { usePricing } from '@/composables/usePricing';
import { processPayment } from '@/app/services/paymentService';
import { hasStripePrice } from '@/app/services/payment/providers/stripe';

const { proxy } = getCurrentInstance();
const { $constants } = proxy;
const { getProductInfo, userZone, pricingConfig } = usePricing();

const dialog = ref(null);
const isProcessing = ref(false);
const currentStep = ref('form'); // 'form', 'providers', 'success'
const context = ref(''); // 'employabilite', 'entrepreneur', 'certification'
const selectedProductId = ref('');
const selectedProviderId = ref('');
const certificationConfig = ref(null);
const customAmount = ref(null);

const providerMetadata = {
  fedapay: {
    id: 'fedapay',
    label: 'FedaPay',
    subtitle: 'Mobile Money (Orange, MTN, Moov)',
    icon: '/images/payments/fedapay.png'
  },
  stripe: {
    id: 'stripe',
    label: 'Stripe',
    subtitle: 'Carte bancaire (Visa, Mastercard)',
    icon: '/images/payments/stripe.png'
  },
  paypal: {
    id: 'paypal',
    label: 'PayPal',
    subtitle: 'Compte PayPal ou Carte',
    icon: '/images/payments/paypal.png'
  },
  quote: {
    id: 'quote',
    label: 'Demander un devis',
    subtitle: 'Paiement ultérieur par facture',
    icon: '/images/icons/document-text.svg'
  },
  none: {
    id: 'none',
    label: 'Inscription Gratuite',
    subtitle: 'Aucun paiement requis',
    icon: '/images/icons/gift.svg'
  }
};

const form = reactive({
  fullName: '',
  email: '',
  phone: '',
  message: ''
});

// Produits disponibles selon le contexte
const availableProducts = computed(() => {
  if (context.value === 'employabilite') {
    return [
      getProductInfo('employabilite_101'),
      getProductInfo('employabilite_201'),
      getProductInfo('employabilite_301')
    ]
      .filter(Boolean)
      .map((p, i) => ({ ...p, id: `employabilite_${[101, 201, 301][i]}` }));
  }
  if (context.value === 'entrepreneur') {
    return [
      getProductInfo('entrepreneur_101'),
      getProductInfo('entrepreneur_201'),
      getProductInfo('entrepreneur_301')
    ]
      .filter(Boolean)
      .map((p, i) => ({ ...p, id: `entrepreneur_${[101, 201, 301][i]}` }));
  }
  if (context.value === 'certification' && selectedProductId.value) {
    const p = getProductInfo(selectedProductId.value);
    return p ? [{ ...p, id: selectedProductId.value }] : [];
  }
  if (context.value === 'donations' && selectedProductId.value) {
    const p = getProductInfo(selectedProductId.value);
    const amount = customAmount.value || (p ? p.prices[userZone.value] : 0);
    return p
      ? [
          {
            ...p,
            id: selectedProductId.value,
            amount,
            formattedPrice: new Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency:
                userZone.value === 'xof'
                  ? 'XOF'
                  : userZone.value === 'eur'
                    ? 'EUR'
                    : 'CAD',
              minimumFractionDigits: 0
            }).format(amount)
          }
        ]
      : [];
  }
  return [];
});

const currentProduct = computed(() => {
  return (
    availableProducts.value.find((p) => p.id === selectedProductId.value) ||
    availableProducts.value[0]
  );
});

const availableProviders = computed(() => {
  if (!currentProduct.value) return [];

  const providers = currentProduct.value.providers?.[userZone.value] || [];
  const providerIds = typeof providers === 'string' ? [providers] : providers;

  return providerIds
    .map((id) => providerMetadata[id])
    .filter(Boolean)
    .filter((p) => {
      // Filtrage spécifique pour Stripe (nécessite un Price ID pré-généré)
      if (p.id === 'stripe') {
        const zone = pricingConfig.zones[userZone.value];
        // Vérification si le montant est personnalisé (ex: don avec un montant libre)
        const basePrice =
          pricingConfig.products[currentProduct.value.id]?.prices?.[
            userZone.value
          ];
        const isCustomAmount =
          currentProduct.value.amount !== undefined &&
          currentProduct.value.amount !== basePrice;

        if (isCustomAmount) {
          return false;
        }

        return hasStripePrice(currentProduct.value.id, zone.currency);
      }
      return true;
    });
});

// UI Dynamique
const title = computed(() => {
  if (context.value === 'certification')
    return certificationConfig.value?.title || 'Certification';
  if (context.value === 'entrepreneur') return 'Parcours Entrepreneur';
  if (context.value === 'donations') return 'Faire un Don';
  return 'Parcours EMPLOYABILITÉ';
});

const subtitle = computed(() => {
  if (context.value === 'certification')
    return 'Validez vos compétences et boostez votre carrière';
  if (context.value === 'donations')
    return 'Votre soutien permet de transformer des vies en Afrique';
  return "Commencez votre parcours vers l'excellence professionnelle";
});

const headerIcon = computed(() => {
  if (context.value === 'certification')
    return certificationConfig.value?.icon || '/images/icons/award.svg';
  if (context.value === 'entrepreneur') return '/images/icons/rocket.svg';
  if (context.value === 'donations') return '/images/icons/heart.svg';
  return '/images/icons/graduation-cap.svg';
});

// Handlers
const handleOpen = (payload) => {
  context.value = payload.context || 'employabilite';
  selectedProductId.value = payload.productId || '';
  certificationConfig.value = payload.certificationConfig || null;
  customAmount.value = payload.customAmount || null;

  // Pré-remplissage si fourni
  if (payload.customerInfo) {
    form.fullName =
      payload.customerInfo.fullName || payload.customerInfo.name || '';
    form.email = payload.customerInfo.email || '';
    form.phone = payload.customerInfo.phone || '';
    form.message = payload.customerInfo.message || '';
  }

  // Reset state
  currentStep.value = 'form';
  isProcessing.value = false;
  selectedProviderId.value = '';

  dialog.value.show();
};

/**
 * Étape 1 -> Étape 2 (ou direct paiement si un seul choix)
 */
const handleNextStep = async () => {
  if (!selectedProductId.value) return;

  // Si plusieurs providers -> direction l'écran de choix
  if (availableProviders.value.length > 1) {
    currentStep.value = 'providers';
    return;
  }

  // Sinon (un seul provider ou aucun) -> paiement direct
  const providerId = availableProviders.value[0]?.id || 'none';
  await handleFinalPayment(providerId);
};

/**
 * Exécution finale du paiement
 */
const handleFinalPayment = async (providerId) => {
  if (isProcessing.value) return;

  selectedProviderId.value = providerId;
  isProcessing.value = true;

  try {
    const result = await processPayment(
      selectedProductId.value,
      userZone.value,
      {
        ...form,
        context: context.value,
        certification: currentProduct.value?.label,
        amount: currentProduct.value?.amount
      },
      providerId
    );

    if (result.success) {
      currentStep.value = 'success';
    } else {
      alert(
        `Erreur: ${result.error || 'Une erreur est survenue lors du paiement.'}`
      );
    }
  } catch (error) {
    console.error('Payment flow error:', error);
    alert('Une erreur inattendue est survenue.');
  } finally {
    isProcessing.value = false;
  }
};

const closeDialog = () => {
  dialog.value.hide();
  // Clear form
  form.fullName = '';
  form.email = '';
  form.phone = '';
  form.message = '';
};
</script>
